import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import DataTable, { TableColumn } from "react-data-table-component";
import { ApiResponse } from "apisauce";
import { toast } from "react-toastify";

import { tableCustomStyles } from "../../utils/table-styles";
import { StreamKey } from "../../utils/constants";
import ticketRangeService from "../../services/ticketRangeService";
import { TicketRangeSummary } from "../../interface/api/TicketRangeSummary";
import { bPrefix, formatTicketNumber } from "../../utils/parser";
import SharedButton from "./Button";

const useStyles = makeStyles({
  // In-table action rendered as a link, matching the `overrideLink` convention the
  // dashboards already use for inline actions.
  rowAction: {
    color: "#1976d2",
    cursor: "pointer",
    textDecoration: "underline",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
});

interface TicketRangesPanelProps {
  stream: StreamKey;
  // Hydrovac only — B-series and regular tickets have separate number spaces.
  isBSeries?: boolean;
  // Jump to the Generate tab with a range prefilled.
  onUseRange?: (from: number, to: number) => void;
  // Bump to force a reload, e.g. after generating.
  refreshToken?: number;
}

// One row per range, generated and missing interleaved in ticket-number order, so the
// sequence reads top to bottom the way the printed tickets run.
interface RangeRow {
  from: number;
  to: number;
  count: number;
  missing: boolean;
}

const formatNumber = (n: number) => n.toLocaleString();

// Shows which ticket numbers have been generated for a stream and where the gaps are,
// with a Generate action on each gap.
const TicketRangesPanel = ({
  stream,
  isBSeries,
  onUseRange,
  refreshToken,
}: TicketRangesPanelProps) => {
  const classes = useStyles();
  const [summary, setSummary] = useState<TicketRangeSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [windowFrom, setWindowFrom] = useState("");
  const [windowTo, setWindowTo] = useState("");

  // Takes the window explicitly so Clear can reload with no filter without waiting for
  // the input state to settle.
  const fetchRanges = useCallback(
    async (fromStr: string, toStr: string) => {
      setLoading(true);
      const from = parseInt(fromStr);
      const to = parseInt(toStr);
      const response: ApiResponse<any> =
        await ticketRangeService.getTicketRanges(stream, {
          isBSeries,
          from: isNaN(from) ? undefined : from,
          to: isNaN(to) ? undefined : to,
        });
      if (response.ok) {
        setSummary(response.data as TicketRangeSummary);
      } else {
        toast.error("Failed to load ticket ranges");
      }
      setLoading(false);
    },
    [stream, isBSeries]
  );

  // The window that has actually been applied, as distinct from what is typed in the
  // boxes. Driving off this (rather than the inputs) is what makes the Generate action
  // safe: typing a From without pressing Apply must not reveal Generate on gaps that
  // were computed unfiltered.
  const [applied, setApplied] = useState({ from: "", to: "" });

  useEffect(() => {
    fetchRanges(applied.from, applied.to);
  }, [fetchRanges, applied, refreshToken]);

  const onApply = () => setApplied({ from: windowFrom, to: windowTo });

  const onClear = () => {
    setWindowFrom("");
    setWindowTo("");
    setApplied({ from: "", to: "" });
  };

  // Ticket numbers use the app-wide padded format (010001), not a thousands
  // separator — formatNumber below is for counts only.
  const label = (n: number) =>
    `${bPrefix({ isBSeries })}${formatTicketNumber(n)}`;

  // Generate is only offered once a From/To has been applied. Unfiltered, a single gap
  // can span hundreds of thousands of numbers — this stops someone generating all of it
  // with one stray click.
  const hasWindow = !!summary && (!!applied.from || !!applied.to);

  const rows: RangeRow[] = summary
    ? [
        ...summary.generated.map((r) => ({ ...r, missing: false })),
        ...summary.missing.map((r) => ({ ...r, missing: true })),
      ].sort((a, b) => a.from - b.from)
    : [];

  const columns: TableColumn<RangeRow>[] = [
    {
      name: "Range",
      selector: (row) => row.from,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <span>
          {row.from === row.to
            ? label(row.from)
            : `${label(row.from)} – ${label(row.to)}`}
        </span>
      ),
    },
    {
      name: "Tickets",
      selector: (row) => row.count,
      sortable: true,
      width: "120px",
      cell: (row) => formatNumber(row.count),
    },
    {
      name: "Status",
      selector: (row) => (row.missing ? 1 : 0),
      sortable: true,
      width: "140px",
      cell: (row) => (
        <Chip
          size="small"
          label={row.missing ? "Missing" : "Generated"}
          style={{
            backgroundColor: row.missing ? "#fdecea" : "#e8f5e9",
            color: row.missing ? "#b71c1c" : "#1b5e20",
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      name: "",
      width: "120px",
      right: true,
      cell: (row) =>
        row.missing && onUseRange && hasWindow ? (
          <span
            role="button"
            tabIndex={0}
            className={classes.rowAction}
            onClick={() => onUseRange(row.from, row.to)}
            onKeyDown={(e) =>
              e.key === "Enter" && onUseRange(row.from, row.to)
            }
          >
            Generate
          </span>
        ) : null,
    },
  ];

  return (
    <Box>
      {/* Same shape as the Generate tab: md={4} cells with the shared full-width
          button, so Apply/Clear match every other form button in the dashboard. */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="From Ticket Number"
            variant="outlined"
            type="number"
            value={windowFrom}
            onChange={(e) => setWindowFrom(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onApply()}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="To Ticket Number"
            variant="outlined"
            type="number"
            value={windowTo}
            onChange={(e) => setWindowTo(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onApply()}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SharedButton
            label="Apply"
            type="button"
            variant="contained"
            color="primary"
            onClick={onApply}
            disabled={loading}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SharedButton
            label="Clear"
            type="button"
            variant="contained"
            color="default"
            onClick={onClear}
            disabled={loading || (!windowFrom && !windowTo)}
          />
        </Grid>
      </Grid>

      {summary && (
        <Box mt={3}>
          <Typography variant="body2" color="textSecondary">
            {summary.highest == null ? (
              <>No tickets generated for this stream yet.</>
            ) : (
              <>
                Generated <b>{label(summary.lowest as number)}</b> to{" "}
                <b>{label(summary.highest)}</b> —{" "}
                <b>{formatNumber(summary.totalGenerated)}</b> tickets,{" "}
                <b>{formatNumber(summary.totalMissing)}</b> missing. Next
                available number is{" "}
                <b>{label(summary.nextAvailable as number)}</b>.
              </>
            )}
          </Typography>

          {summary.totalMissing > 0 && !hasWindow && (
            <Typography variant="caption" color="textSecondary">
              Set a From/To above to generate a missing range — gaps can span hundreds
              of thousands of numbers unfiltered.
            </Typography>
          )}

          <Box mt={2}>
            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              // No pagination — a stream collapses to a handful of ranges.
              <DataTable
                columns={columns}
                data={rows}
                customStyles={tableCustomStyles}
                noDataComponent={
                  <Box padding={2}>
                    No tickets generated for this stream yet.
                  </Box>
                }
                dense
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TicketRangesPanel;
